// Helpers for blog post content: generate a Table of Contents from the
// article HTML and inject matching `id` attributes into each <h2> so the
// anchor links actually scroll to the right heading.

const slugify = (text) =>
  text
    .toLowerCase()
    .replace(/<[^>]+>/g, '') // strip any HTML inside the heading
    .replace(/[^\w\s-]/g, '') // drop punctuation
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 80);

/**
 * Parse article HTML for <h2> headings and return:
 *   - `contentWithIds`: the HTML with `id="..."` injected into each <h2>
 *   - `toc`: array of { id, text } entries (headings in order)
 */
export const buildToc = (html) => {
  if (!html) return { contentWithIds: '', toc: [] };

  const seen = new Map();
  const toc = [];

  const contentWithIds = html.replace(
    /<h2(\s[^>]*)?>([\s\S]*?)<\/h2>/gi,
    (match, attrs = '', inner) => {
      // Don't overwrite an existing id.
      const hasId = attrs && /\sid\s*=/.test(attrs);
      const text = inner.replace(/<[^>]+>/g, '').trim();
      if (!text) return match;

      let id = slugify(text) || `section-${toc.length + 1}`;
      const count = (seen.get(id) || 0) + 1;
      seen.set(id, count);
      if (count > 1) id = `${id}-${count}`;

      toc.push({ id, text });
      if (hasId) return match;
      return `<h2${attrs || ''} id="${id}">${inner}</h2>`;
    }
  );

  return { contentWithIds, toc };
};

/** Approximate word count for an HTML string. */
export const wordCount = (html) => {
  if (!html) return 0;
  return html
    .replace(/<[^>]+>/g, ' ')
    .split(/\s+/)
    .filter(Boolean).length;
};
